import { Grow, Paper } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import { DropZone, FILE_TYPES, SwalContext } from 'genesis-suite/components'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useRef, useState } from 'react'
import XLSX from 'xlsx'
import Loading from '../components/Loading'
import { AuthContext } from '../components/contexts/AuthContext'
import { CloudContext } from '../components/contexts/CloudContext'
import { ContextDetails } from '../components/manage-users'
import AddUserModal from '../components/manage-users/AddUserModal'
import SecurityFiltersModal from '../components/manage-users/SecurityFiltersModal'
import UserTable from '../components/manage-users/UserTable'
import { logEvent } from '../lib/amplitudeClient'
import { userService } from '../lib/services'
import { default as roles, default as userRoles } from '../lib/userRoles'
import UploadExcelDialog from './UploadExcelDialog'

const styles = theme => ({
    container: {
        height: '100%',
        display: 'flex',
        flexFlow: 'column nowrap',
        padding: theme.spacing(1),
    },
    tableContainer: {
        display: 'flex',
        flex: '1 1 auto',
        overflow: 'auto',
    },
    addUserFab: {
        position: 'absolute',
        right: theme.spacing(1),
        bottom: theme.spacing(1),
    },
    filterWrap: {
        flex: '1 0 auto',
        overflow: 'hidden',
        maxHeight: 500,
    },
    summary: {
        flex: '0 0 auto',
    },
    DZPaper: {
        top: 165,
        right: 15,
        padding: theme.spacing(1),
        position: 'absolute',
        '& > div': { padding: theme.spacing(1) },
    },
})

const ManageUsers = props => {
    const { classes } = props
    const [users, setUsers] = useState(null)

    const [loading, setLoading] = useState(true)
    const [changes, stageChanges] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [updateUsers, setUpdateUsers] = useState(false)

    const [selectedUser, setSelectedUser] = useState(null)
    const [showAddUserModal, setShowAddUserModal] = useState(false)
    const [DZOpen, setDZOpen] = useState(false)
    const [fileConfig, setFileConfig] = useState(null)
    const [excelHeader, setExcelHeader] = useState(null)
    const [excelData, setExcelData] = useState([])
    const [userDataList, setUserDataList] = useState([])
    const [selectedUserList, setSelectedUserList] = useState([])
    const userTableRef = useRef(null)
    const [searchTerm, setSearchTerm] = useState(null)
    const { user } = useContext(AuthContext)

    const { alert, confirm } = useContext(SwalContext)
    const { selectedCloud: context } = useContext(CloudContext)
    const { enqueueSnackbar: showSnackbar, closeSnackbar } = useSnackbar()
    const errCallback = err => {
        alert(err.Message || err.message, { type: 'error' })
        setLoading(false)
    }
    //TODO Remove this once the backend team fixes the cloud name key property
    const text = context.charAt(0).toLowerCase() + context.slice(1)
    useEffect(() => {
        let cancelCall = () => {}
        if (context) {
            setLoading(true)
            setSelectedUser(null)
            stageChanges(null)
            userService
                .getUsers(context, cancel => {
                    cancelCall = cancel
                })
                .then(userObjs => {
                    const users = userObjs
                        .map((userObj, i) => ({
                            ...userObj.userInfo,
                            id: !userObj.userInfo.id ? i + 1 : userObj.userInfo.id,
                            role: roles[userObj.userInfo.permissions[context]],
                            isSelected: false,
                            createdBy: userObj.userInfo.permissionChanges[text].createdBy,
                            modifiedBy: userObj.userInfo.permissionChanges[text].modifiedBy
                                ? userObj.userInfo.permissionChanges[text].modifiedBy
                                : userObj.userInfo.permissionChanges[text].createdBy,
                        }))
                        .filter(user => user.role !== 'Owner')
                    setUsers(users)
                    setLoading(false)
                })
                .catch(err => {
                    if (err.__CANCEL__) return
                    errCallback(err)
                })
        }
        return () => cancelCall()
    }, [context, updateUsers])

    if (loading) return <Loading title="Loading..." />

    const handleChange = user => {
        stageChanges({
            ...changes,
            [user.userId]: { ...user, contextName: context },
        })
    }

    const handleSubmit = () => {
        if (changes) {
            logEvent('Save Users')
            setSubmitting(true)
            const promises = []
            Object.values(changes).map(user => promises.push(userService.saveUser(user)))
            Promise.all(promises)
                .then(() => {
                    setSubmitting(false)
                    stageChanges(null)
                    alert('User(s) saved successfully!', { type: 'success' })
                })
                .catch(err => {
                    setSubmitting(false)
                    alert(err.Message || err.message, { type: 'error' })
                })
        }
    }

    const handleShowAddUserModal = () => {
        if (changes) {
            alert(
                'You have unsaved changes to existing users. Please save your changes or discard before adding new users.',
                { type: 'warning', title: 'Unsaved Changes' }
            )
            return
        }

        setShowAddUserModal(true)
    }

    const handleCloseAddUserModal = newEntry => {
        if (newEntry) setUpdateUsers(updateUsers ? false : true) // cause fresh data fetch
        setShowAddUserModal(false)
    }

    const toggleUserFilters = user => {
        setSelectedUser({ ...user, contextName: context })
    }

    const handleUserCheckbox = userIds => {
        setSelectedUserList(userIds)
    }

    const onDrop = async ([file]) => {
        var reader = new FileReader()
        const rABS = !!reader.readAsBinaryString
        reader.onload = function (e) {
            var contents = e.target.result
            const wb = XLSX.read(contents, { type: rABS ? 'binary' : 'array' })
            const wsname = wb.SheetNames[0]
            const ws = wb.Sheets[wsname]
            /* Convert array of arrays */
            const data = XLSX.utils.sheet_to_json(ws)
            setExcelData(data)
            const excelHeader = data?.[0]
            setExcelHeader(excelHeader)

            const fileName = file.name.substring(0, file.name.lastIndexOf('.'))
            const totalRecords = data?.length
            setFileConfig({ fileName, totalRecords })
        }
        reader.readAsBinaryString(file)
        setDZOpen(false)
    }

    const handleCloseUploadDialog = changes => {
        if (changes) setUpdateUsers(updateUsers ? false : true) // cause fresh data fetch
        setFileConfig(null)
    }

    const handleExcelSubmit = totalRecords => {
        userService
            .saveUsers(userDataList)
            .then(() => {
                handleCloseUploadDialog(true)
                showSnackbar(`${totalRecords} users saved successfully. `, { variant: 'success' })
            })
            .catch(err => {
                alert(err.Message || err.message, { type: 'error' })
                handleCloseUploadDialog(false)
                showSnackbar('Error occured while saving users. Please try again later', { variant: 'error' })
            })
    }

    const validateAndSetData = mappings => {
        let newUserList = []
        for (let user of excelData) {
            //Creating new user with excel data but keys replaced with data field keys used for API call
            const parsedUSer = {}
            mappings.forEach(field => {
                parsedUSer[field.fieldName] = user[field.columnName]
            })

            //Validating required fields -> sending error message if missing
            if (!parsedUSer.userId || !parsedUSer.firstName || !parsedUSer.email || !parsedUSer.lastName) {
                return 'Required fields are missing in Excel. Please try re-uploading with changes.'
            }
            //Validating 'Role' -> sending error message if not valid and setting default as User if field empty in excel
            parsedUSer.permission = parsedUSer.permission
                ? Object.keys(userRoles).find(
                      key => userRoles[key].toLowerCase() === parsedUSer.permission.toLowerCase()
                  )
                : 1
            if (!parsedUSer.permission) {
                return 'Roles can only be from User, Super User or Admin.'
            }
            //Validating 'External login'-> sending error message if not valid and setting default as false if field empty in excel
            if (!parsedUSer.isExternal || ['n', 'false', 'no'].includes(parsedUSer.isExternal?.toLowerCase()))
                parsedUSer.isExternal = false
            else if (['y', 'true', 'yes'].includes(parsedUSer.isExternal.toLowerCase())) parsedUSer.isExternal = true
            else
                return 'External Login can be either (true or false) or (yes or no). Please try re-uploading with changes.'

            //preparing userBody to call API
            const { permissions = {} } = parsedUSer
            const userBody = {
                ...parsedUSer,
                contextName: context,
                phone: parsedUSer.phone ? parsedUSer.phone : '--',
                //include both existing permissions for existing users and permission from the new user form
                //so that this method can be reused between both forms
                permissions: { ...permissions, [context]: parsedUSer.permission || 1 },
                isCoreSuperUser: false,
            }
            //Adding all users to the list
            newUserList.push(userBody)
        }

        setUserDataList(newUserList)

        return null
    }

    const submitAddUserInfo = user => {
        const { permissions = {} } = user
        const userBody = {
            ...user,
            contextName: context,
            phone: user.phone ? user.phone : '--',
            //include both existing permissions for existing users and permission from the new user form
            //so that this method can be reused between both forms
            permissions: { ...permissions, [context]: user.permission || 1 },
            isCoreSuperUser: false,
        }

        return userService.saveUser(userBody)
    }

    const handleCreateNewUser = user => {
        logEvent('Add User')
        submitAddUserInfo(user)
            .then(() => handleCloseAddUserModal(true))
            .catch(err => {
                alert(err.Message || err.message, { type: 'error' })
                handleCloseAddUserModal(false)
            })
    }

    const handleDeleteUsers = async () => {
        const response = await confirm('Are you sure you want to delete the selected users?', {
            type: 'warning',
            confirmButtonProps: { text: 'Delete' },
        })
        if (response.dismiss) return

        if (!users) {
            alert('There were no users selected to delete!', { type: 'error' })
            return
        }
        logEvent('Delete Users')
        const usersToDelete = users.filter(user => selectedUserList.includes(user.id)).map(user => user.userId)
        setLoading(true)

        try {
            for (const userId of usersToDelete) {
                await userService.deleteUser(context, userId)
            }
        } catch (err) {
            alert(err.Message || err.message, { type: 'error' })
        }

        setUpdateUsers(prevUpdateUsers => (prevUpdateUsers ? false : true)) // cause fresh data fetch
    }

    const exportUsers = async exportType => {
        const key = showSnackbar('Downloading users. Please wait...', { persist: true })
        try {
            const res = await userService.export(context, searchTerm, exportType, user.userId)
        } catch (e) {
            console.error(e)
            showSnackbar(`Unable to export users. Please try again later`, { variant: 'error' })
        } finally {
            closeSnackbar(key)
            userTableRef?.current?.handleExportPopoverClose()
        }
    }

    const handleSearch = value => {
        setSearchTerm(value)
    }

    return (
        <div className={classes.container}>
            <ContextDetails className={classes.summary} context={context} users={users} />
            <div className={classes.tableContainer}>
                <UserTable
                    ref={userTableRef}
                    users={users}
                    selectedUser={selectedUser}
                    context={context}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onShowAddUserModal={handleShowAddUserModal}
                    onUserFilterSelect={toggleUserFilters}
                    onUserCheckbox={handleUserCheckbox}
                    onDeleteUsers={handleDeleteUsers}
                    toggleUploadFile={() => setDZOpen(!DZOpen)}
                    buttonState={{
                        disabled: !changes || submitting,
                        submitting,
                    }}
                    selectedUserList={selectedUserList}
                    handleSearch={handleSearch}
                    exportUsers={exportUsers}
                />
                <SecurityFiltersModal
                    user={selectedUser}
                    onChange={user => {
                        setSelectedUser(user)
                        setUsers(
                            users.map(u => {
                                if (u.userId === user.userId) {
                                    return { ...u, ...user }
                                }
                                return u
                            })
                        )

                        if (changes && changes[user.userId]) {
                            stageChanges({
                                ...changes,
                                [user.userId]: { ...user, contextName: context },
                            })
                        }
                    }}
                    onClose={() => setSelectedUser(null)}
                />
            </div>

            <AddUserModal
                requireContext
                users={users}
                open={showAddUserModal}
                onSubmit={handleCreateNewUser}
                onClose={handleCloseAddUserModal}
            />

            <Grow in={DZOpen}>
                <Paper elevation={4} className={classes.DZPaper}>
                    <DropZone
                        onDrop={onDrop}
                        accept={FILE_TYPES.EXCEL}
                        message="Drop an Excel file or click to choose"
                        onClose={() => setDZOpen(null)}
                    />
                </Paper>
            </Grow>

            <UploadExcelDialog
                validate={validateAndSetData}
                updateFile={onDrop}
                fileConfig={fileConfig}
                onSubmit={handleExcelSubmit}
                formFields={formFields}
                excelColumnsList={excelHeader}
                onCancel={() => setFileConfig(null)}
            />
        </div>
    )
}

export default withStyles(styles)(ManageUsers)

const formFields = [
    {
        key: 1,
        dataField: 'userId',
        text: 'Username',
        isRequired: true,
    },
    {
        key: 2,
        dataField: 'screenAlias',
        text: 'Display Name',
    },
    {
        key: 3,
        dataField: 'firstName',
        text: 'First',
        isRequired: true,
    },
    {
        key: 4,
        dataField: 'lastName',
        text: 'Last',
    },
    {
        key: 5,
        dataField: 'email',
        text: 'Email',
        isRequired: true,
    },
    {
        key: 6,
        dataField: 'phone',
        text: 'Phone',
    },
    {
        key: 7,
        dataField: 'isExternal',
        text: 'External Login',
        deafult: false,
    },
    {
        key: 8,
        dataField: 'permission',
        text: 'Role',
        default: 'User',
    },
]
